import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { User, Trophy, TrendingUp, TrendingDown, Briefcase, BarChart3, Calendar, ArrowRight } from 'lucide-react';
import { usersAPI } from '../services/api';

const UserProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchProfile(); }, [username]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const [profileRes, holdingsRes] = await Promise.all([
        usersAPI.getProfile(username),
        usersAPI.getPortfolio(username),
      ]);
      setProfile(profileRes.data);
      setHoldings(holdingsRes.data || []);
    } catch {}
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E17]">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-8"><div className="h-64 bg-[#121826] rounded-2xl border border-[#1E293B] animate-pulse" /></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#0A0E17]">
        <Header />
        <div className="max-w-5xl mx-auto px-4 py-16 text-center">
          <h2 className="text-[#F8FAFC] text-2xl font-heading mb-4">User Not Found</h2>
          <Link to="/leaderboards"><Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black">View Leaderboards</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E17]">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-[#121826] rounded-2xl border border-[#1E293B] p-8 mb-8" data-testid="user-profile-card">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-[#F59E0B] flex items-center justify-center">
              <span className="text-black font-bold text-3xl font-heading">{profile.username?.slice(0,2).toUpperCase()}</span>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h1 className="font-heading text-3xl font-medium text-[#F8FAFC] mb-1" data-testid="profile-username">{profile.username}</h1>
              {profile.rank && <span className="text-[#F59E0B] text-sm font-medium">Rank #{profile.rank}</span>}
              <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start text-[#64748B] text-sm">
                <Calendar className="w-4 h-4" />
                <span>Joined {profile.joined_at ? new Date(profile.joined_at).toLocaleDateString() : 'Recently'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Net Worth', value: `B₹${((profile.net_worth || 0) / 100000).toFixed(1)}L`, icon: BarChart3, color: '#F59E0B' },
            { label: 'Portfolio Value', value: `B₹${((profile.portfolio_value || 0) / 100000).toFixed(1)}L`, icon: Briefcase, color: '#8B5CF6' },
            { label: 'Total Trades', value: (profile.total_trades || 0).toString(), icon: TrendingUp, color: '#10B981' },
            { label: 'Rank', value: profile.rank ? `#${profile.rank}` : 'Unranked', icon: Trophy, color: '#F59E0B' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-[#121826] rounded-2xl border border-[#1E293B] p-5" data-testid={`profile-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-[#64748B] text-sm">{label}</span>
              </div>
              <span className="text-[#F8FAFC] font-bold text-2xl font-mono">{value}</span>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-[#121826] rounded-2xl border border-[#1E293B] overflow-hidden" data-testid="user-holdings">
            <div className="bg-[#1E293B] px-6 py-4">
              <h2 className="text-[#F8FAFC] font-bold text-lg font-heading">Holdings ({holdings.length})</h2>
            </div>
            {holdings.length > 0 ? (
              <div className="divide-y divide-[#1E293B]">
                {holdings.map((h) => (
                  <Link key={h.symbol} to={`/security/${h.symbol}`} className="flex items-center gap-4 px-6 py-4 hover:bg-[#1E293B]/50 transition-colors">
                    <div className="w-10 h-10 bg-[#1E293B] rounded-lg flex items-center justify-center">
                      <span className="text-[#F59E0B] font-bold text-xs font-mono">{h.symbol?.slice(0,3)}</span>
                    </div>
                    <div className="flex-1">
                      <div className="text-[#F8FAFC] font-semibold text-sm">{h.name || h.symbol}</div>
                      <div className="text-[#64748B] text-xs">{h.shares} shares</div>
                    </div>
                    <span className="text-[#F8FAFC] font-bold font-mono text-sm">B₹{(h.current_price || 0).toFixed(2)}</span>
                    <ArrowRight className="w-4 h-4 text-[#64748B]" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-[#64748B]">No holdings</div>
            )}
          </div>

          <div className="bg-[#121826] rounded-2xl border border-[#1E293B] overflow-hidden" data-testid="user-trades">
            <div className="bg-[#1E293B] px-6 py-4">
              <h2 className="text-[#F8FAFC] font-bold text-lg font-heading">Recent Trades</h2>
            </div>
            {profile.recent_trades?.length > 0 ? (
              <div className="divide-y divide-[#1E293B]">
                {profile.recent_trades.map((t, i) => (
                  <div key={i} className="px-6 py-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[#F59E0B] font-mono text-sm font-bold">{t.symbol}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.trade_type === 'buy' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>
                        {t.trade_type?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-[#64748B]">
                      <span>{t.shares} shares</span>
                      <span className="font-mono">B₹{(t.price || 0).toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-[#64748B]">No recent trades</div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserProfile;
